import React from 'react';
import fhirpath from 'fhirpath';

import { PassoExecutado } from '@/models/PassoExecutado';

import { ArtifactFormOverrides } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.types';
import { useArtifactFormConfig } from '@/features/artifacts/hooks/useArtifactFormConfig';
import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import { clinicalDocumentPdf } from '@/features/pdf/documents/ClinicalDocumentLayout';
import DocumentPageEncaminhamento from '@/features/pdf/documents/DocumentPageEncaminhamento';

import Feature from '@/features/features/components/Feature';
import IconButton from '@/components/IconButton';
import { FiPrinter } from 'react-icons/fi';
import CardSolicitacao, {
  CardSolicitacaoProxyProps,
} from '../../CardSolicitacao';
import SolicitacaoList from '..';

const Form: ArtifactFormOverrides = [
  {
    name: 'reasonCode',
    label: 'Indicação clínica',
    attrs: { autoFocus: true },
  },
];

const CardEncaminhamento: React.FC<CardSolicitacaoProxyProps> = ({
  solicitacao,
  ...rest
}) => {
  const form = useArtifactFormConfig('encaminhamento', Form);
  const { user } = useAuth();
  const [{ encounter }] = useEnterpriseContext();

  const [printing, setPrinting] = React.useState(false);

  const title = React.useMemo(() => {
    return (
      fhirpath.evaluate(
        solicitacao.pedido,
        'code.select(text | coding.display).first()',
      )[0] || 'Encaminhamento '
    );
  }, [solicitacao.pedido]);

  const description = React.useMemo(() => {
    const reason = fhirpath.evaluate(
      solicitacao.pedido,
      'ServiceRequest.reasonCode.select(text | coding.display).first()',
    );
    if (reason.length) {
      return reason[0];
    }

    return '';
  }, [solicitacao.pedido]);

  const print = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      try {
        setPrinting(true);

        await clinicalDocumentPdf(DocumentPageEncaminhamento, {
          user,
          resource: solicitacao.pedido as fhir4.ServiceRequest,
          encounter: encounter.data,
        });
      } catch (error) {
        throw new Error(`Erro ao gerar PDF do encaminhamento: ${error}`);
      } finally {
        setPrinting(false);
      }
    },
    [user, solicitacao.pedido, encounter.data],
  );

  const renderPrintSection = React.useCallback(() => {
    return (
      <Feature name="solicitacoes.encaminhamento.can_print">
        <IconButton
          disabled={printing}
          tooltip="Imprimir"
          onClick={print}
          icon={<FiPrinter />}
        />
      </Feature>
    );
  }, [print, printing]);

  return (
    <CardSolicitacao
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
      title={title}
      description={description}
      solicitacao={solicitacao}
      form={form}
      renderActions={renderPrintSection}
    />
  );
};

interface Props {
  passo: PassoExecutado;
}
const SolicitacaoListEncaminhamento: React.FC<Props> = ({ passo }) => {
  return (
    <SolicitacaoList
      passo={passo}
      tipo="encaminhamento"
      CardSolicitacaoComponent={CardEncaminhamento}
      groupTitle="Selecione os encaminhamentos que você quer solicitar no seu prontuário."
      addConfig={{
        title: 'Adicionar encaminhamento complementar',
      }}
    />
  );
};

export default SolicitacaoListEncaminhamento;
